/**
 * Controller for resilient playback: one `<audio>` element owns sound and
 * the playback clock through its own Hls instance on the independent audio
 * child, and one muted `<video>` element plays the video-only child through
 * a second Hls instance, following the audio clock. See `resilientPlayback.ts`
 * for the pure mode/drift decisions this hook feeds and applies.
 *
 * Never plays master-playlist audio at the same time as this independent
 * audio — a caller that mounts this hook must not also attach a third Hls
 * instance to the master for its AUDIO group.
 *
 * Requires `Hls.isSupported()` — the caller (V2Player's dispatcher) must
 * fall back to the legacy single-element player when hls.js isn't usable
 * (e.g. Safari's native HLS), since native playback exposes no per-fragment
 * gap/PROGRAM-DATE-TIME data this controller's mode decisions depend on.
 */
import Hls from 'hls.js';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { PlayerTransport } from '../PlayerChrome';
import type { TimeRange } from './availability';
import { entrySeekClampIntent, EntryIntent } from './entryIntent';
import { utcToMediaSeconds } from './firstOverlap';
import {
  FragmentAvailability,
  ResilientMode,
  decideMode,
  isPublishedAt,
  isTimeBuffered,
  isWithinSyncTolerance,
  latestContiguousPublishedRange,
  MAX_MEDIA_RECOVERIES,
  MEDIA_RECOVERY_RESET_MS,
  monotonicMountRev,
  MountedRevision,
  needsResync,
  parseMasterManifest,
  resolveChildPlaylistUrl,
  SYNC_RESYNC_THRESHOLD_MS,
} from './resilientPlayback';
import {
  clampSeekSeconds,
  DEFAULT_SEGMENT_DURATION_S,
  liveEdgeUtcMsFromRegion,
  LIVE_EDGE_SYNC_SEGMENTS,
  pickLiveEdgeRegion,
} from './seekClamp';

/** Same bounded budget as the legacy single-instance player — see V2Player.tsx. */
const MAX_NETWORK_RETRIES = 3;
/** A resync/explicit seek that never resolves falls back to allowing another attempt after this. */
const SEEK_PENDING_TIMEOUT_MS = 10_000;
/** Cadence for the mode/drift decision loop — coarser than a timeupdate tick so it reads settled state. */
const TICK_INTERVAL_MS = 250;
/** Same as the legacy player: bound long-running live playback's memory instead of hls.js's Infinity default. */
const BACK_BUFFER_LENGTH_S = 30;
/** A published rendition whose element has been starved this long without buffered data to fall back on is treated as unavailable, not just momentarily stalled. */
const STALL_BOUND_MS = 2_000;
/** Give up on a run of corrective seeks that keep missing rather than fight hls.js forever — the next mode change or explicit seek gets a fresh budget. */
const MAX_CORRECTIVE_SEEKS = 3;
/** A resync's seek target is aimed this far past the audio UTC to absorb the seek's own travel time, refined from the previous seek's measured duration once known. */
const DEFAULT_SEEK_DURATION_ESTIMATE_MS = 300;
const MIN_SEEK_DURATION_ESTIMATE_MS = 100;
const MAX_SEEK_DURATION_ESTIMATE_MS = 2_000;

export interface UseResilientPlaybackOptions {
  masterUrl: string;
  snapshotRev?: number | null;
  /** Coverage's `coverage_life` — see `monotonicMountRev`. */
  coverageLife?: string | null;
  live?: boolean;
  startAtLiveEdge?: boolean;
  autoPlay?: boolean;
  muted?: boolean;
  /** Coverage's `playable_union` (UTC ms) — feeds the live-edge clamp exactly as the legacy player's `playableRangesSeconds` does, never mode decisions. */
  playableUnion?: TimeRange[];
  /** One-shot deep-link/resume seek, applied once the first anchor is known — see the legacy player's own `initialSeekUtcMs` contract. */
  initialSeekUtcMs?: number | null;
  /** Why this mount is opening where it is — see `entryIntent.ts`. Required,
   *  same as the legacy player: governs the entry-point seek's clamp
   *  behavior and whether this controller places itself at the live edge. */
  entryIntent: EntryIntent;
  onUtcUpdate?: (utcMs: number | null) => void;
  onPdtReady?: (utcMs: number) => void;
  onMediaState?: (mode: ResilientMode) => void;
  onFatalError?: (detail: string) => void;
}

export interface UseResilientPlaybackHandle {
  seekToUtc: (utcMs: number) => void;
  play: () => void;
  pause: () => void;
}

export interface UseResilientPlaybackResult {
  audioRef: React.MutableRefObject<HTMLAudioElement | null>;
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  mode: ResilientMode;
  /** Whether the video picture is trustworthy enough to show right now (buffered and in sync, or the sole clock source). */
  videoReady: boolean;
  fatal: string | null;
  transport: PlayerTransport;
  handle: UseResilientPlaybackHandle;
  /** Seeks to the newest playable position, the same live-edge affordance the legacy player's "Go live" button drives. */
  goLive: () => void;
}

function bufferedRangesUtc(media: HTMLMediaElement, anchorUtcMs: number | null): TimeRange[] {
  if (anchorUtcMs === null) return [];
  const ranges: TimeRange[] = [];
  const buffered = media.buffered;
  for (let i = 0; i < buffered.length; i += 1) {
    ranges.push([anchorUtcMs + buffered.start(i) * 1000, anchorUtcMs + buffered.end(i) * 1000]);
  }
  return ranges;
}

export function useResilientPlayback(options: UseResilientPlaybackOptions): UseResilientPlaybackResult {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const [mode, setMode] = useState<ResilientMode>('waiting');
  const [videoReady, setVideoReady] = useState(false);
  const [fatal, setFatal] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [audioCanPlay, setAudioCanPlay] = useState(false);
  const [videoCanPlay, setVideoCanPlay] = useState(false);
  const [chromeMuted, setChromeMuted] = useState(options.muted ?? false);
  const [volume, setVolume] = useState(0.8);
  // Specifically the AUDIO child's own anchor — audio is the seek's actual
  // target, so gating on "whichever child anchors first" (as `onPdtReady`
  // does) could apply the seek before audio even has an anchor to seek on,
  // silently dropping it if video's playlist happens to load first.
  const [audioAnchorReady, setAudioAnchorReady] = useState(false);

  const modeRef = useRef<ResilientMode>('waiting');
  const videoReadyRef = useRef(false);
  const isPlayingRef = useRef(false);
  const clockUtcRef = useRef<number | null>(null);
  const generationRef = useRef(0);
  const previousMountRef = useRef<MountedRevision | null>(null);
  // The initialSeekUtcMs value already applied — undefined lets a fresh
  // mount apply the current value even if it numerically matches whatever
  // a PRIOR session happened to leave here.
  const appliedSeekRef = useRef<number | null | undefined>(undefined);

  const audioHlsRef = useRef<Hls | null>(null);
  const videoHlsRef = useRef<Hls | null>(null);
  const audioAnchorRef = useRef<number | null>(null);
  const videoAnchorRef = useRef<number | null>(null);
  const primaryAnchorRef = useRef<number | null>(null);
  const audioFragmentsRef = useRef<FragmentAvailability[]>([]);
  const videoFragmentsRef = useRef<FragmentAvailability[]>([]);
  // EXT-X-TARGETDURATION off each child's own playlist, for the live-edge backoff below.
  const audioTargetDurationRef = useRef<number | null>(null);
  const videoTargetDurationRef = useRef<number | null>(null);
  const videoGivenUpRef = useRef(false);
  const audioSeekPendingRef = useRef(false);
  const videoSeekPendingRef = useRef(false);
  const audioSeekTimeoutIdRef = useRef<number | null>(null);
  const videoSeekTimeoutIdRef = useRef<number | null>(null);
  const audioStalledSinceRef = useRef<number | null>(null);
  const videoStalledSinceRef = useRef<number | null>(null);
  const correctiveSeekCountRef = useRef(0);
  const videoSeekIssuedAtRef = useRef<number | null>(null);
  const videoSeekDurationEstimateRef = useRef(DEFAULT_SEEK_DURATION_ESTIMATE_MS);
  // Only a corrective (drift/stall-recovery) seek's measured duration
  // should refine the "aim ahead" estimate — an explicit seek to a
  // far-away, likely-unbuffered target can legitimately take much longer
  // to settle, which would otherwise overshoot every later small correction.
  const isCorrectiveVideoSeekRef = useRef(false);
  // Set on a fresh 'live' mount with no initialSeekUtcMs; cleared once the
  // live-edge placement actually runs, or a real explicit seek preempts it.
  // Never set on a revision reload — that path resumes the captured UTC.
  const livePlacementPendingRef = useRef(false);

  const liveRef = useRef(options.live ?? false);
  liveRef.current = options.live ?? false;
  const startAtLiveEdgeRef = useRef(options.startAtLiveEdge ?? false);
  startAtLiveEdgeRef.current = options.startAtLiveEdge ?? false;
  const autoPlayRef = useRef(options.autoPlay ?? false);
  autoPlayRef.current = options.autoPlay ?? false;
  const playableUnionRef = useRef<TimeRange[]>(options.playableUnion ?? []);
  playableUnionRef.current = options.playableUnion ?? [];
  const callbacksRef = useRef({
    onUtcUpdate: options.onUtcUpdate,
    onPdtReady: options.onPdtReady,
    onMediaState: options.onMediaState,
    onFatalError: options.onFatalError,
  });
  callbacksRef.current = {
    onUtcUpdate: options.onUtcUpdate,
    onPdtReady: options.onPdtReady,
    onMediaState: options.onMediaState,
    onFatalError: options.onFatalError,
  };

  const masterUrl = options.masterUrl;
  const snapshotRev = options.snapshotRev ?? null;
  const coverageLife = options.coverageLife ?? null;
  const initialSeekUtcMs = options.initialSeekUtcMs;
  const entryIntent = options.entryIntent;

  // playableUnion (coverage's playable_union, UTC ms) converted to media
  // seconds via one element's own anchor — mirrors the legacy player's
  // `playableRangesSeconds`, fed to `clampSeekSeconds` so the gap-aware
  // live edge never lands somewhere nothing will ever load. Reads the ref
  // (not `options.playableUnion` directly) so a caller passed outside the
  // main effect's own closures still sees the latest coverage.
  const playableRangesSecondsFor = (anchorUtcMs: number | null): Array<[number, number]> => {
    if (anchorUtcMs === null) return [];
    return playableUnionRef.current.map(
      ([start, end]) => [utcToMediaSeconds(start, anchorUtcMs), utcToMediaSeconds(end, anchorUtcMs)] as [number, number],
    );
  };

  // The pending flag's safety net: armed the instant a seek is ISSUED, not
  // when a `seeking` DOM event fires — an element that never dispatches
  // `seeking` for some reason would otherwise leave the flag stuck forever,
  // permanently blocking every future corrective seek and hiding the
  // picture for good. The `seeking` listener re-arms it defensively but is
  // no longer the only thing that starts the clock.
  const clearAudioSeekTimeout = () => {
    if (audioSeekTimeoutIdRef.current !== null) {
      window.clearTimeout(audioSeekTimeoutIdRef.current);
      audioSeekTimeoutIdRef.current = null;
    }
  };
  const armAudioSeekTimeout = () => {
    clearAudioSeekTimeout();
    audioSeekTimeoutIdRef.current = window.setTimeout(() => {
      audioSeekPendingRef.current = false;
      audioSeekTimeoutIdRef.current = null;
    }, SEEK_PENDING_TIMEOUT_MS);
  };
  const clearVideoSeekTimeout = () => {
    if (videoSeekTimeoutIdRef.current !== null) {
      window.clearTimeout(videoSeekTimeoutIdRef.current);
      videoSeekTimeoutIdRef.current = null;
    }
  };
  const armVideoSeekTimeout = () => {
    clearVideoSeekTimeout();
    videoSeekTimeoutIdRef.current = window.setTimeout(() => {
      videoSeekPendingRef.current = false;
      videoSeekTimeoutIdRef.current = null;
    }, SEEK_PENDING_TIMEOUT_MS);
  };

  // Audio always accepts the seek (it owns the clock); marks it pending so
  // the automatic videoOnly-recovery resync doesn't stack a second seek
  // on top of one still resolving.
  const seekAudioToUtc = (utcMs: number, intent: 'explicit' | 'resume' = 'explicit') => {
    const audio = audioRef.current;
    if (!audio || audioAnchorRef.current === null) return;
    audioSeekPendingRef.current = true;
    armAudioSeekTimeout();
    audio.currentTime = clampSeekSeconds(audio, (utcMs - audioAnchorRef.current) / 1000, {
      live: liveRef.current,
      intent,
      playableRanges: playableRangesSecondsFor(audioAnchorRef.current),
    });
  };

  // Always issues the seek — whether the target is buffered yet is not a
  // condition here: an explicit seek always calls this, and the tick only
  // calls it when the target is published and under the corrective-seek
  // cap. Refusing to seek an unbuffered target would mean hls.js never
  // learns to fetch it. `corrective` marks a tick-driven drift/stall
  // correction (as opposed to an explicit/resume seek) — only those feed
  // the seek-duration estimate (see `onVideoSeeked`).
  const seekVideoToUtc = (utcMs: number, intent: 'explicit' | 'resume' = 'explicit', corrective = false) => {
    const video = videoRef.current;
    if (!video || videoAnchorRef.current === null) return;
    videoSeekPendingRef.current = true;
    armVideoSeekTimeout();
    videoSeekIssuedAtRef.current = performance.now();
    isCorrectiveVideoSeekRef.current = corrective;
    video.currentTime = clampSeekSeconds(video, (utcMs - videoAnchorRef.current) / 1000, {
      live: liveRef.current,
      intent,
      playableRanges: playableRangesSecondsFor(videoAnchorRef.current),
    });
  };

  // The host's explicit seek path (scrubber, lap jump, deep link). Always
  // moves both elements toward the target and resets the corrective-seek
  // budget — a deliberate seek is a fresh intent, not a drift correction.
  const seekToUtcExternal = (utcMs: number, intent: 'explicit' | 'resume' = 'explicit') => {
    if (!Number.isFinite(utcMs)) return;
    // seekAudioToUtc/seekVideoToUtc each no-op without their own anchor, so
    // this call does nothing at all when NEITHER has one yet — that must
    // not cancel a pending automatic live placement (a no-op caller seek
    // pressed before any data has loaded would otherwise strand the player
    // with neither seek ever applied). A real caller-driven seek that DOES
    // move at least one element always preempts the pending placement —
    // landing there after the viewer has already moved elsewhere would
    // silently discard their seek.
    if (audioAnchorRef.current !== null || videoAnchorRef.current !== null) {
      livePlacementPendingRef.current = false;
    }
    correctiveSeekCountRef.current = 0;
    seekAudioToUtc(utcMs, intent);
    seekVideoToUtc(utcMs, intent);
  };

  const mountedSnapshotRev = monotonicMountRev(previousMountRef.current, masterUrl, coverageLife, snapshotRev);

  useEffect(() => {
    const audio = audioRef.current;
    const video = videoRef.current;
    if (!audio || !video) return undefined;

    const myGeneration = generationRef.current + 1;
    generationRef.current = myGeneration;
    const isCurrent = () => generationRef.current === myGeneration;

    const previousMount = previousMountRef.current;
    // A new life is a different recording, so it resets rather than reloads in place.
    const isRevisionReload =
      previousMount !== null &&
      previousMount.masterUrl === masterUrl &&
      previousMount.life === coverageLife &&
      previousMount.rev !== mountedSnapshotRev;
    previousMountRef.current = { masterUrl, life: coverageLife, rev: mountedSnapshotRev };

    const resume = isRevisionReload
      ? {
          utcMs: clockUtcRef.current,
          paused: !isPlayingRef.current,
          muted: audio.muted,
          volume: audio.volume,
        }
      : null;

    setFatal(null);
    modeRef.current = 'waiting';
    setMode('waiting');
    videoReadyRef.current = false;
    setVideoReady(false);
    setAudioAnchorReady(false);
    if (!isRevisionReload) {
      appliedSeekRef.current = undefined;
    }
    // A revision reload resumes the captured UTC instead (see `resume`
    // above) — only a fresh 'live' mount with nothing else to apply defers
    // to the live-edge placement below.
    livePlacementPendingRef.current =
      !isRevisionReload && entryIntent === 'live' && startAtLiveEdgeRef.current && initialSeekUtcMs == null;
    clockUtcRef.current = null;
    audioAnchorRef.current = null;
    videoAnchorRef.current = null;
    primaryAnchorRef.current = null;
    audioFragmentsRef.current = [];
    videoFragmentsRef.current = [];
    audioTargetDurationRef.current = null;
    videoTargetDurationRef.current = null;
    videoGivenUpRef.current = false;
    audioSeekPendingRef.current = false;
    videoSeekPendingRef.current = false;
    clearAudioSeekTimeout();
    clearVideoSeekTimeout();
    audioStalledSinceRef.current = null;
    videoStalledSinceRef.current = null;
    correctiveSeekCountRef.current = 0;
    videoSeekIssuedAtRef.current = null;
    videoSeekDurationEstimateRef.current = DEFAULT_SEEK_DURATION_ESTIMATE_MS;
    isCorrectiveVideoSeekRef.current = false;
    let audioRetries = 0;
    let videoRetries = 0;

    const buildHlsConfig = () => ({
      enableWorker: true,
      lowLatencyMode: false,
      liveSyncDurationCount: liveRef.current && startAtLiveEdgeRef.current ? LIVE_EDGE_SYNC_SEGMENTS : 999,
      liveMaxLatencyDurationCount: 10000,
      startPosition: liveRef.current && startAtLiveEdgeRef.current ? -1 : 0,
      // Sparse video is EXT-X-GAP-heavy by design in resilient mode — walk across it.
      maxBufferHole: 6,
      backBufferLength: BACK_BUFFER_LENGTH_S,
    });

    // Fires `onPdtReady` from whichever child's anchor lands first — a
    // general "the timeline has SOME known anchor now" signal for hosts.
    // Deliberately separate from `audioAnchorReady` below: the initial-seek
    // effect must wait specifically for AUDIO's own anchor, not this one.
    const noteAnchorReady = (utcMs: number) => {
      if (primaryAnchorRef.current === null) {
        primaryAnchorRef.current = utcMs;
        callbacksRef.current.onPdtReady?.(utcMs);
      }
    };

    // Applies the same live-edge target "Go live" uses (see `goLive` below),
    // once the OWNING medium's own anchor is known — Step 7's rule (an entry
    // seek only ever applies after the relevant anchor lands) applies here
    // too, not just to `initialSeekUtcMs`. Also waits for the FOLLOWER
    // element's own anchor (unless it has definitively given up) before
    // committing — placing only the owner would strand the follower at time
    // 0, never catching up to the live edge once its anchor lands afterward,
    // since the pending flag is a one-shot. Runs on every anchor/bounds-
    // relevant event but only ever acts once, since `livePlacementPendingRef`
    // is cleared the instant it fires (or a real explicit seek preempts it —
    // see `seekToUtcExternal`).
    const attemptLivePlacement = () => {
      if (!isCurrent() || !livePlacementPendingRef.current) return;
      const audioRegion = latestContiguousPublishedRange(audioFragmentsRef.current);
      const videoRegion = audioRegion ? null : latestContiguousPublishedRange(videoFragmentsRef.current);
      const region = pickLiveEdgeRegion(audioRegion, videoRegion);
      if (!region) return; // nothing published at either tail yet — keep waiting
      const usingVideo = !audioRegion;
      const ownAnchor = usingVideo ? videoAnchorRef.current : audioAnchorRef.current;
      if (ownAnchor === null) return; // wait for the OWNING medium's own anchor
      const followerAnchor = usingVideo ? audioAnchorRef.current : videoAnchorRef.current;
      const followerGivenUp = usingVideo ? false : videoGivenUpRef.current; // audio is mandatory, never "given up"
      if (followerAnchor === null && !followerGivenUp) return;
      livePlacementPendingRef.current = false;
      goLive();
    };

    // Turns a level's fragment list into UTC-anchored availability, and
    // resolves the shared anchor from whichever child's playlist reports a
    // PROGRAM-DATE-TIME first (mirrors V2Player's single-instance anchor).
    const trackLevel = (
      fragmentsRef: React.MutableRefObject<FragmentAvailability[]>,
      anchorRef: React.MutableRefObject<number | null>,
      targetDurationRef: React.MutableRefObject<number | null>,
      onFirstAnchor: (utcMs: number) => void,
    ) => {
      return (
        _event: unknown,
        data: {
          details?: {
            fragments?: Array<{ programDateTime: number | null; start: number; duration: number; gap?: boolean }>;
            targetduration?: number;
          };
        },
      ) => {
        if (!isCurrent()) return;
        if (typeof data.details?.targetduration === 'number') {
          targetDurationRef.current = data.details.targetduration;
        }
        const fragments = data.details?.fragments ?? [];
        // Resolve a first-time anchor from the raw fragments before touching
        // `fragmentsRef` below, but defer telling the caller (`onFirstAnchor`)
        // until AFTER `fragmentsRef.current` is updated — a caller that reacts
        // by reading `fragmentsRef.current` (the live-edge placement) must see
        // this level's fragments, not the stale ones from before this call.
        let freshAnchorUtcMs: number | null = null;
        if (anchorRef.current === null) {
          const withPdt = fragments.find((f) => typeof f.programDateTime === 'number');
          if (withPdt && typeof withPdt.programDateTime === 'number') {
            anchorRef.current = withPdt.programDateTime - withPdt.start * 1000;
            freshAnchorUtcMs = anchorRef.current;
          }
        }
        const anchor = anchorRef.current;
        const next: FragmentAvailability[] = [];
        for (const f of fragments) {
          const startUtcMs = typeof f.programDateTime === 'number' ? f.programDateTime : anchor !== null ? anchor + f.start * 1000 : null;
          if (startUtcMs === null) continue;
          next.push({ startUtcMs, endUtcMs: startUtcMs + f.duration * 1000, gap: !!f.gap });
        }
        fragmentsRef.current = next;
        if (freshAnchorUtcMs !== null) onFirstAnchor(freshAnchorUtcMs);
      };
    };

    const attachErrorHandling = (hls: Hls, onNetworkExhausted: (detail: string) => void, retryCounter: () => number, bumpRetry: () => void) => {
      // Per instance, so a reload starts with a fresh budget.
      let mediaRecoveries = 0;
      let lastRecoveryAt = 0;
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (!isCurrent() || !data.fatal) return;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          bumpRetry();
          if (retryCounter() > MAX_NETWORK_RETRIES) {
            hls.stopLoad();
            onNetworkExhausted(data.details ?? 'network error');
          } else {
            hls.startLoad();
          }
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          // Exhausting the budget exits the same way a dead network does: for the
          // video instance that means giving up quietly and staying audio-only.
          const now = Date.now();
          if (now - lastRecoveryAt > MEDIA_RECOVERY_RESET_MS) mediaRecoveries = 0;
          lastRecoveryAt = now;
          mediaRecoveries += 1;
          if (mediaRecoveries > MAX_MEDIA_RECOVERIES) {
            hls.stopLoad();
            onNetworkExhausted(data.details ?? 'media error');
          } else {
            hls.recoverMediaError();
          }
        } else {
          onNetworkExhausted(data.details ?? 'playback failed');
        }
      });
    };

    const startAudio = (url: string) => {
      if (!isCurrent()) return;
      const hls = new Hls(buildHlsConfig());
      audioHlsRef.current = hls;
      hls.on(
        Hls.Events.LEVEL_LOADED,
        trackLevel(audioFragmentsRef, audioAnchorRef, audioTargetDurationRef, (utcMs) => {
          noteAnchorReady(utcMs);
          setAudioAnchorReady(true);
          if (resume) {
            if (resume.utcMs !== null) seekAudioToUtc(resume.utcMs, 'resume');
            audio.muted = resume.muted;
            audio.volume = resume.volume;
            if (!resume.paused) void audio.play().catch(() => undefined);
          }
          attemptLivePlacement();
        }),
      );
      hls.on(
        Hls.Events.LEVEL_UPDATED,
        trackLevel(audioFragmentsRef, audioAnchorRef, audioTargetDurationRef, () => undefined),
      );
      attachErrorHandling(
        hls,
        (detail) => {
          setFatal(detail);
          callbacksRef.current.onFatalError?.(detail);
        },
        () => audioRetries,
        () => {
          audioRetries += 1;
        },
      );
      hls.loadSource(url);
      hls.attachMedia(audio);
      // Mute/volume are NOT re-applied from the `muted` prop here — the
      // mirroring effect below already applied whatever the chrome's
      // CURRENT state is at mount, and stays in sync afterward. Re-applying
      // the prop here would silently undo a mute/unmute the viewer toggled
      // during the async master fetch this function runs after.
      if (!isRevisionReload && autoPlayRef.current) {
        void audio.play().catch(() => undefined);
      }
    };

    const startVideo = (url: string) => {
      if (!isCurrent()) return;
      video.muted = true; // the video-only rendition carries no audio track; this only helps autoplay policy.
      const hls = new Hls(buildHlsConfig());
      videoHlsRef.current = hls;
      hls.on(
        Hls.Events.LEVEL_LOADED,
        trackLevel(videoFragmentsRef, videoAnchorRef, videoTargetDurationRef, (utcMs) => {
          noteAnchorReady(utcMs);
          if (resume?.utcMs != null) {
            seekVideoToUtc(resume.utcMs, 'resume');
          }
          attemptLivePlacement();
        }),
      );
      hls.on(
        Hls.Events.LEVEL_UPDATED,
        trackLevel(videoFragmentsRef, videoAnchorRef, videoTargetDurationRef, () => undefined),
      );
      attachErrorHandling(
        hls,
        () => {
          // Video is optional in resilient mode — give up quietly and stay audio-only
          // instead of surfacing a fatal error for a picture that was never required.
          videoGivenUpRef.current = true;
          videoFragmentsRef.current = [];
        },
        () => videoRetries,
        () => {
          videoRetries += 1;
        },
      );
      hls.loadSource(url);
      hls.attachMedia(video);
    };

    const controller = new AbortController();
    let cancelled = false;
    void (async () => {
      let text: string;
      try {
        const response = await fetch(masterUrl, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error(`master responded ${response.status}`);
        text = await response.text();
      } catch (err) {
        if (cancelled || !isCurrent()) return;
        const message = err instanceof Error ? err.message : 'master fetch failed';
        setFatal(message);
        callbacksRef.current.onFatalError?.(message);
        return;
      }
      if (cancelled || !isCurrent()) return;
      const { audioUri, videoUri } = parseMasterManifest(text);
      if (!audioUri) {
        const message = 'master playlist has no independent audio rendition';
        setFatal(message);
        callbacksRef.current.onFatalError?.(message);
        return;
      }
      startAudio(resolveChildPlaylistUrl(masterUrl, audioUri));
      if (videoUri) {
        startVideo(resolveChildPlaylistUrl(masterUrl, videoUri));
      } else {
        videoGivenUpRef.current = true;
      }
    })();

    const onAudioPlay = () => {
      isPlayingRef.current = true;
      setIsPlaying(true);
      if (!videoGivenUpRef.current) void video.play().catch(() => undefined);
    };
    const onAudioPause = () => {
      isPlayingRef.current = false;
      setIsPlaying(false);
      video.pause();
    };
    const onAudioRateChange = () => {
      video.playbackRate = audio.playbackRate;
    };
    const onAudioCanPlay = () => setAudioCanPlay(true);
    const onVideoCanPlay = () => setVideoCanPlay(true);
    const onAudioWaiting = () => {
      audioStalledSinceRef.current = performance.now();
    };
    const onAudioPlaying = () => {
      audioStalledSinceRef.current = null;
    };
    const onVideoWaiting = () => {
      videoStalledSinceRef.current = performance.now();
    };
    const onVideoPlaying = () => {
      videoStalledSinceRef.current = null;
    };

    const syncFromOwner = () => {
      const owner = modeRef.current === 'videoOnly' ? video : audio;
      setPosition(owner.currentTime);
      setDuration(Number.isFinite(owner.duration) ? owner.duration : 0);
      attemptLivePlacement();
    };

    // `seeking` re-arms the same safety timeout the issuing seek functions
    // already armed — defensive re-affirmation for a seek that somehow
    // reached the element outside those functions, not the sole starter of
    // the clock (see `armAudioSeekTimeout`/`armVideoSeekTimeout` above).
    const onAudioSeeking = () => {
      audioSeekPendingRef.current = true;
      armAudioSeekTimeout();
    };
    const onAudioSeeked = () => {
      audioSeekPendingRef.current = false;
      clearAudioSeekTimeout();
    };
    const onVideoSeeking = () => {
      videoSeekPendingRef.current = true;
      armVideoSeekTimeout();
    };
    const onVideoSeeked = () => {
      videoSeekPendingRef.current = false;
      clearVideoSeekTimeout();
      // Refines the "aim ahead" estimate from how long THIS seek actually
      // took to settle — only for a corrective seek. An explicit seek to a
      // far-away, likely-unbuffered target can legitimately take far
      // longer, which would otherwise overshoot every later small
      // correction by up to MAX_SEEK_DURATION_ESTIMATE_MS.
      if (videoSeekIssuedAtRef.current !== null) {
        if (isCorrectiveVideoSeekRef.current) {
          const elapsed = performance.now() - videoSeekIssuedAtRef.current;
          videoSeekDurationEstimateRef.current = Math.min(
            MAX_SEEK_DURATION_ESTIMATE_MS,
            Math.max(MIN_SEEK_DURATION_ESTIMATE_MS, elapsed),
          );
        }
        videoSeekIssuedAtRef.current = null;
      }
      isCorrectiveVideoSeekRef.current = false;
    };

    audio.addEventListener('play', onAudioPlay);
    audio.addEventListener('pause', onAudioPause);
    audio.addEventListener('ratechange', onAudioRateChange);
    audio.addEventListener('canplay', onAudioCanPlay);
    audio.addEventListener('waiting', onAudioWaiting);
    audio.addEventListener('playing', onAudioPlaying);
    audio.addEventListener('seeking', onAudioSeeking);
    audio.addEventListener('seeked', onAudioSeeked);
    audio.addEventListener('timeupdate', syncFromOwner);
    audio.addEventListener('durationchange', syncFromOwner);
    audio.addEventListener('loadedmetadata', syncFromOwner);
    audio.addEventListener('seeked', syncFromOwner);
    video.addEventListener('canplay', onVideoCanPlay);
    video.addEventListener('waiting', onVideoWaiting);
    video.addEventListener('playing', onVideoPlaying);
    video.addEventListener('timeupdate', syncFromOwner);
    video.addEventListener('durationchange', syncFromOwner);
    video.addEventListener('loadedmetadata', syncFromOwner);
    video.addEventListener('seeked', syncFromOwner);
    video.addEventListener('seeking', onVideoSeeking);
    video.addEventListener('seeked', onVideoSeeked);

    // Mode/drift decision loop. Runs on a fixed cadence rather than off
    // audio's own `timeupdate` alone — a stalled/gapped audio element stops
    // firing `timeupdate` at a useful rate right when detecting "video now
    // owns the clock" matters most.
    const tick = () => {
      if (!isCurrent()) return;
      const audioAnchor = audioAnchorRef.current;
      const videoAnchor = videoAnchorRef.current;
      const audioUtc = audioAnchor !== null ? audioAnchor + audio.currentTime * 1000 : null;
      const videoUtc = videoAnchor !== null ? videoAnchor + video.currentTime * 1000 : null;

      // A rendition counts as available only if the playlist actually
      // publishes it AND it's either already buffered or hasn't been
      // starved for more than STALL_BOUND_MS — a coverage-endpoint read is
      // never part of this; only the two children's own playlists and
      // buffered ranges are (see the brief's "coverage is a hint, not proof").
      const audioStalled =
        audioStalledSinceRef.current !== null && performance.now() - audioStalledSinceRef.current > STALL_BOUND_MS;
      const audioBufferedNow =
        audioUtc !== null && audioAnchor !== null && isTimeBuffered(bufferedRangesUtc(audio, audioAnchor), audioUtc);
      const audioPublished = audioUtc !== null && isPublishedAt(audioFragmentsRef.current, audioUtc);
      const audioAvailable = audioPublished && (audioBufferedNow || !audioStalled);

      const referenceUtc = audioAvailable ? audioUtc : videoUtc;

      const videoStalled =
        videoStalledSinceRef.current !== null && performance.now() - videoStalledSinceRef.current > STALL_BOUND_MS;
      const videoBufferedNow =
        referenceUtc !== null && videoAnchor !== null && isTimeBuffered(bufferedRangesUtc(video, videoAnchor), referenceUtc);
      const videoPublished = referenceUtc !== null && isPublishedAt(videoFragmentsRef.current, referenceUtc);
      const videoAvailable = !videoGivenUpRef.current && videoPublished && (videoBufferedNow || !videoStalled);

      const nextMode = decideMode({ audioAvailable, videoAvailable });
      const prevMode = modeRef.current;
      // Reset the corrective-seek budget as soon as a mode change is
      // DETECTED (before anything below reads it this same tick) — a
      // waiting/audioOnly/videoOnly -> both transition must not have its
      // very first correction wiped out by the mode-change bookkeeping.
      const modeChanged = nextMode !== prevMode;
      if (modeChanged) {
        correctiveSeekCountRef.current = 0;
      }

      // Video was the only clock while audio was gone (stalled or genuinely
      // gapped) — once audio has real, PUBLISHED media at the UTC video was
      // holding, resume it there instead of leaving it wherever it stalled.
      // Requiring "published at videoUtc" (not just "audio available at its
      // OWN position") stops a ping-pong against hls.js's own gap-skipping:
      // hls.js can jump a stalled audio element past its hole on its own,
      // landing audioAvailable=true at a position AHEAD of videoUtc: seeking
      // audio back into videoUtc there would drive it straight back into the
      // hole it just escaped. Audio is never seeked backward otherwise.
      if (
        prevMode === 'videoOnly' &&
        nextMode !== 'videoOnly' &&
        audioAvailable &&
        videoUtc !== null &&
        !audioSeekPendingRef.current &&
        isPublishedAt(audioFragmentsRef.current, videoUtc)
      ) {
        seekAudioToUtc(videoUtc);
      }

      let nextVideoReady: boolean;
      if (nextMode === 'both' && audioUtc !== null && videoUtc !== null && videoAnchor !== null) {
        const drift = videoUtc - audioUtc;
        const bufferedAtTarget = isTimeBuffered(bufferedRangesUtc(video, videoAnchor), audioUtc);
        // Hysteresis: once shown, a drift that has crept back up to the
        // resync threshold (500ms) doesn't hide the picture again — only
        // getting it visible the FIRST time needs the tighter 250ms bound.
        // Without this, the 250-500ms band hid a picture that needed no
        // correction at all.
        const withinHysteresis = videoReadyRef.current
          ? Math.abs(drift) <= SYNC_RESYNC_THRESHOLD_MS
          : isWithinSyncTolerance(drift);
        nextVideoReady = !videoSeekPendingRef.current && bufferedAtTarget && withinHysteresis;

        // "Only when buffered" gates repeated corrective resyncs once
        // synced, not whether video ever LOADS the target — a video that is
        // published but not yet buffered at the audio UTC still needs a
        // real seek to make hls.js fetch that region at all (e.g. after a
        // large scrub or a lap jump). Bounded so a target that keeps
        // missing doesn't fight hls.js forever; the cap clears on the next
        // mode change or explicit seek.
        if (!videoSeekPendingRef.current && needsResync(drift, isPublishedAt(videoFragmentsRef.current, audioUtc))) {
          if (correctiveSeekCountRef.current < MAX_CORRECTIVE_SEEKS) {
            correctiveSeekCountRef.current += 1;
            // Aim past the audio UTC by the seek's own expected travel time
            // so landing doesn't immediately re-drift by that same amount.
            seekVideoToUtc(audioUtc + videoSeekDurationEstimateRef.current, 'explicit', true);
          }
        }
      } else {
        nextVideoReady = nextMode === 'videoOnly';

        // audioOnly can mean "video is genuinely gapped" OR "video is
        // published but stalled/unbuffered at the audio UTC" (mode fell out
        // of 'both' via the stall/buffered check above, not via a real
        // gap). The latter needs the same nudge the 'both' branch gives a
        // scrubbed-to target — otherwise a stalled video sits frozen
        // forever with nothing ever telling it to load the current
        // position, since the 'both'-only corrective logic never runs here.
        if (
          nextMode === 'audioOnly' &&
          audioUtc !== null &&
          videoAnchor !== null &&
          !videoSeekPendingRef.current &&
          isPublishedAt(videoFragmentsRef.current, audioUtc) &&
          correctiveSeekCountRef.current < MAX_CORRECTIVE_SEEKS
        ) {
          correctiveSeekCountRef.current += 1;
          seekVideoToUtc(audioUtc + videoSeekDurationEstimateRef.current, 'explicit', true);
        }
      }

      if (modeChanged) {
        modeRef.current = nextMode;
        setMode(nextMode);
        callbacksRef.current.onMediaState?.(nextMode);
      }
      if (nextVideoReady) correctiveSeekCountRef.current = 0;
      if (nextVideoReady !== videoReadyRef.current) {
        videoReadyRef.current = nextVideoReady;
        setVideoReady(nextVideoReady);
      }

      const clockUtc = nextMode === 'videoOnly' ? videoUtc : audioUtc;
      if (clockUtc !== clockUtcRef.current) {
        clockUtcRef.current = clockUtc;
        callbacksRef.current.onUtcUpdate?.(clockUtc);
      }
    };
    const intervalId = window.setInterval(tick, TICK_INTERVAL_MS);

    return () => {
      cancelled = true;
      generationRef.current += 1; // invalidate this generation for any in-flight async work
      controller.abort();
      window.clearInterval(intervalId);
      clearAudioSeekTimeout();
      clearVideoSeekTimeout();
      audio.removeEventListener('play', onAudioPlay);
      audio.removeEventListener('pause', onAudioPause);
      audio.removeEventListener('ratechange', onAudioRateChange);
      audio.removeEventListener('canplay', onAudioCanPlay);
      audio.removeEventListener('waiting', onAudioWaiting);
      audio.removeEventListener('playing', onAudioPlaying);
      audio.removeEventListener('seeking', onAudioSeeking);
      audio.removeEventListener('seeked', onAudioSeeked);
      audio.removeEventListener('timeupdate', syncFromOwner);
      audio.removeEventListener('durationchange', syncFromOwner);
      audio.removeEventListener('loadedmetadata', syncFromOwner);
      audio.removeEventListener('seeked', syncFromOwner);
      video.removeEventListener('canplay', onVideoCanPlay);
      video.removeEventListener('waiting', onVideoWaiting);
      video.removeEventListener('playing', onVideoPlaying);
      video.removeEventListener('timeupdate', syncFromOwner);
      video.removeEventListener('durationchange', syncFromOwner);
      video.removeEventListener('loadedmetadata', syncFromOwner);
      video.removeEventListener('seeked', syncFromOwner);
      video.removeEventListener('seeking', onVideoSeeking);
      video.removeEventListener('seeked', onVideoSeeked);
      audioHlsRef.current?.destroy();
      audioHlsRef.current = null;
      videoHlsRef.current?.destroy();
      videoHlsRef.current = null;
    };
  }, [masterUrl, coverageLife, mountedSnapshotRev]);

  // Entry-point seek — a deep link, saved resume position, or lap/stage
  // entry point. Gated on AUDIO's own anchor specifically (not "whichever
  // child anchors first") — audio is the actual seek target, and applying
  // this before its anchor exists would silently no-op the audio half
  // (`seekAudioToUtc` requires `audioAnchorRef`) whenever video's playlist
  // happens to load first, with no second chance since `appliedSeekRef`
  // would already read as applied. Applies once per distinct value, marked
  // applied only after the seek is actually issued; re-fires for a
  // genuinely new value (see `appliedSeekRef`'s reset rules in the main
  // effect above).
  useEffect(() => {
    if (initialSeekUtcMs == null || !audioAnchorReady) return;
    if (appliedSeekRef.current === initialSeekUtcMs) return;
    seekToUtcExternal(initialSeekUtcMs, entrySeekClampIntent(entryIntent));
    appliedSeekRef.current = initialSeekUtcMs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSeekUtcMs, audioAnchorReady]);

  // Volume/mute live on the audio element (the sound owner) whenever the
  // shared chrome's state changes, exactly like the legacy player's single
  // element — this also applies the initial volume/mute on mount, since
  // both start populated from `options.muted`/the default volume.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = chromeMuted;
    audio.volume = volume;
  }, [chromeMuted, volume]);

  // Same target the automatic live placement applies: the latest contiguous
  // PUBLISHED audio region (video only when audio has none), backed off ~2
  // segment durations, clamped inside that region. Seeks whichever element
  // owns the region off its OWN anchor first, then brings the other element
  // to the same UTC target if its anchor is known too — audio is preferred
  // but never assumed to be the only medium with something to say.
  const goLive = () => {
    const video = videoRef.current;
    const audioRegion = latestContiguousPublishedRange(audioFragmentsRef.current);
    const videoRegion = audioRegion ? null : latestContiguousPublishedRange(videoFragmentsRef.current);
    const region = pickLiveEdgeRegion(audioRegion, videoRegion);
    // Neither medium has a published run — a live playlist can legitimately
    // be all-gap for a stretch (unclaimed cells past their grace period).
    // Seeking against raw `seekable`/`duration` here would land on media
    // that was never actually published; do nothing instead and let the
    // button work once real data exists. Crucially, this must NOT clear a
    // pending automatic placement — a press that lands here did nothing,
    // and cancelling the pending flag anyway would strand the player with
    // neither this seek nor the automatic one ever applied.
    if (!region) return;

    const usingVideo = !audioRegion;
    const targetDurationS = usingVideo ? videoTargetDurationRef.current : audioTargetDurationRef.current;
    const targetUtcMs = liveEdgeUtcMsFromRegion(region, targetDurationS ?? DEFAULT_SEGMENT_DURATION_S);
    if (usingVideo) {
      if (!video || videoAnchorRef.current === null) return; // wait for video's own anchor
      livePlacementPendingRef.current = false;
      correctiveSeekCountRef.current = 0;
      seekVideoToUtc(targetUtcMs);
      if (audioAnchorRef.current !== null) seekAudioToUtc(targetUtcMs);
      return;
    }
    if (audioAnchorRef.current === null) return; // wait for audio's own anchor
    livePlacementPendingRef.current = false;
    correctiveSeekCountRef.current = 0;
    seekAudioToUtc(targetUtcMs);
    if (videoAnchorRef.current !== null) seekVideoToUtc(targetUtcMs);
  };

  const handle = useMemo<UseResilientPlaybackHandle>(
    () => ({
      seekToUtc: (utcMs: number) => seekToUtcExternal(utcMs, 'explicit'),
      play: () => {
        void audioRef.current?.play().catch(() => undefined);
      },
      pause: () => {
        audioRef.current?.pause();
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const transport: PlayerTransport = {
    isPlaying,
    currentTime: position,
    duration,
    ready: audioCanPlay || videoCanPlay,
    muted: chromeMuted,
    volume,
    togglePlay: () => {
      const audio = audioRef.current;
      if (!audio) return;
      if (audio.paused) void audio.play().catch(() => undefined);
      else audio.pause();
    },
    seek: (seconds) => {
      const ownerAnchor = modeRef.current === 'videoOnly' ? videoAnchorRef.current : audioAnchorRef.current;
      if (ownerAnchor === null) return;
      seekToUtcExternal(ownerAnchor + seconds * 1000);
    },
    setMuted: (nextMuted) => setChromeMuted(nextMuted),
    setVolume: (nextVolume) => {
      setVolume(nextVolume);
      if (nextVolume > 0 && chromeMuted) setChromeMuted(false);
    },
  };

  return { audioRef, videoRef, mode, videoReady, fatal, transport, handle, goLive };
}
